import './style.scss';
import {useTranslation} from 'react-i18next';
import {ClinicAside} from './components/ClinicAside/ClinicAside';
import {ClinicMain} from './components/ClinicMain/ClinicMain';
import {DataActions, useClinicDetailsContext} from '../../context/ClinicDetailsContext';
import {BookingActions, useBookingContext} from '../../context/BookingContext';
import {ClinicHeader} from './components/ClinicHeader/ClinicHeader';
import {Button} from '../form/button/Button';
import ModalPortal from '../layout/modal/ModalPortal';
import {SessionWarningModal} from './components/Session/SessionWarningModal';
import {useEffect, useState} from 'react';
import {OpenSessionWarningModal} from './components/Session/OpenSessionWarningModal';
import {BookingSessionActions, useBookingSessionContext} from '../../context/BookingSessionContext';
import {useSessionContext} from '../../context/SessionContext';
import {PolicyLinks} from '../policy/links/PolicyLinks';
import {CookieConsentModal} from '../cookies/CookieConsent/CookieConsentModal';
import {useCaregiverConsentCookie} from '../../hooks/useCaregiverConsentCookie';
import {ClinicLoading} from './components/ClinicLoading/ClinicLoading';
import {ClinicError} from './components/ClinicError/ClinicError';
import {gTagManager} from 'src/lib/gTagManager/gTagManager';
import {
    AppointmentCategory,
    CaregiverObject,
    ClinicJSON,
    CountyFilterObject,
    MunicipalityFilterObject,
    ReasonFilterObject
} from 'src/services/api/types/types';
import {VisitCategory} from '../main/components/PickVisitCategory';
import {getTextFromLocale} from 'src/services/i18n/locale';
import {injectCustomCSSLink} from 'src/lib/customcss/customcss';

interface ClinicDetailsProps {
    healthDeclarationMode?: boolean;
}

export class NoHealthDeclarationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NoHealthDeclarationError';
    }
}

export function ClinicDetails({healthDeclarationMode = false}: ClinicDetailsProps) {
    const {t} = useTranslation();

    const {
        dataState: {loading, clinic, error, careGivers, visitReasons, appointmentCategories, county, municipality},
        dispatch: clinicDispatch
    } = useClinicDetailsContext();
    const {
        bookingState: {step, bookingId, bookingType},
        dispatch
    } = useBookingContext();
    const {bookingSessionState, refreshBookingSession, bookingSessionDispatch} = useBookingSessionContext();
    const {sessionState} = useSessionContext();
    const [showTermsModal, setShowTermsModal] = useState(false);

    const caregiver = careGivers.find(({id}) => clinic && id === clinic.careGiver);
    const {hasConsent, hasChosen, setCookieConsent} = useCaregiverConsentCookie(
        caregiver?.id as number,
        caregiver?.cookiePolicyVersion as string
    );

    const stepClasses = step ? ` ${step}` : '';

    const ClinicFooter = () => {
        return (
            <footer className='container__footer'>
                {clinic && showTermsModal && (
                    <ModalPortal onCloseClick={() => setShowTermsModal(false)}>
                        <div className='policy-modal'>
                            <h1 className='policy-modal__header'>{t('confirm.policy.header')}</h1>
                            <p className='policy-modal__content wrap-html'>{clinic.termsText}</p>
                        </div>
                    </ModalPortal>
                )}
                <div>
                    <a href={process.env.REACT_APP_LANDING_PAGE_URL} target='_blank' rel='noreferrer'>
                        <img src='/mittvaccin_logo.svg' alt='MittVaccin Logo' />
                        <span className='screen-reader-only'>{t('general.opens_in_new_window')}</span>
                    </a>
                </div>
                <div className='container__footer__links'>
                    <p>
                        <PolicyLinks />
                        {clinic && (
                            <a
                                href='/'
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowTermsModal(true);
                                }}
                            >
                                {t('clinic_footer.clinic_privacy_policy')}
                            </a>
                        )}
                    </p>
                </div>
            </footer>
        );
    };

    useEffect(() => {
        if (loading || clinic === null) {
            return;
        }

        const findVisitReasonsByCategory = (visitReasons: ReasonFilterObject[], category: string) => {
            const visitReasonsToAdd = visitReasons.filter((visitReason) => visitReason?.category === category);
            return visitReasonsToAdd.map((visitReasonToAdd: ReasonFilterObject) =>
                getTextFromLocale(visitReasonToAdd?.name)
            );
        };

        const findVisitReasons = (
            clinicAvailableReasons: VisitCategory[],
            visitReasonsOnClinic: ReasonFilterObject[],
            visitReasonCategory: string,
            serviceText: string,
            servicesText: string[],
            itemLimit = 50
        ) => {
            const hasVaccination = clinicAvailableReasons?.find((category) => category === visitReasonCategory);
            if (hasVaccination) {
                let categoryVisitReasons = findVisitReasonsByCategory(visitReasonsOnClinic, visitReasonCategory);

                if (categoryVisitReasons.length > 0) {
                    if (categoryVisitReasons.length > itemLimit) {
                        categoryVisitReasons.length = itemLimit; // Limit number of items
                    }

                    const categoryVisitReasonsText = categoryVisitReasons
                        .join(', ')
                        .replace(/,(?!.*,)/gim, ' ' + t('meta.and'));
                    const text = serviceText + ' (' + categoryVisitReasonsText + ')';
                    servicesText.push(text);
                }
            }
            return servicesText;
        };

        const getClinicServicesText = (
            availableVisitReasons: number[],
            hasDropin: boolean,
            visitReasons: ReasonFilterObject[]
        ): string => {
            let services = '';

            if (availableVisitReasons.length > 0 && hasDropin) {
                services += ' ' + t('meta.clinic.visit_reason_booking_and_drop_in');
            } else if (availableVisitReasons.length > 0 && !hasDropin) {
                services += ' ' + t('meta.clinic.visit_reason_booking');
            } else if (hasDropin) {
                // Does not happen at this point as drop-in categories will be listed as availableVisitReasons
                services += ' ' + t('meta.clinic.visit_reason_drop_in');
            }

            // Visit Reason Categories
            let visitReasonsOnClinic: ReasonFilterObject[] = [];

            availableVisitReasons.forEach((visitReasonId: number) => {
                const visitReasonToAdd = visitReasons.find((visitReason) => +visitReason.id === visitReasonId);
                if (visitReasonToAdd !== undefined) {
                    visitReasonsOnClinic.push(visitReasonToAdd);
                }
            });

            const uniqueVisitReasonCategories = visitReasons.filter(
                (value, index, self) => index === self.findIndex((t) => t.category === value.category)
            );
            let clinicAvailableReasons = uniqueVisitReasonCategories?.map((reason) => reason.category);
            if (clinicAvailableReasons.length > 0) {
                let servicesText: string[] = [];

                servicesText = findVisitReasons(
                    clinicAvailableReasons,
                    visitReasonsOnClinic,
                    'vaccination',
                    t('meta.vaccination'),
                    servicesText
                );
                servicesText = findVisitReasons(
                    clinicAvailableReasons,
                    visitReasonsOnClinic,
                    'travel',
                    t('meta.travel'),
                    servicesText
                );
                servicesText = findVisitReasons(
                    clinicAvailableReasons,
                    visitReasonsOnClinic,
                    'other',
                    t('meta.other'),
                    servicesText
                );

                if (servicesText.length > 0) {
                    let servicesTextCombined = servicesText
                        .join(', ')
                        .replace(/, ([^,]*)$/, ' ' + t('meta.and') + ' $1');

                    services += ' ' + t('meta.for') + ' ' + servicesTextCombined;
                }
            }
            if (services !== '') {
                services = services + '.';
            }

            return services;
        };

        const clinicInfoText = (
            clinic: ClinicJSON,
            careGivers: CaregiverObject[],
            county: CountyFilterObject | null,
            municipality: MunicipalityFilterObject | null
        ) => {
            let careGiverName = '';
            let location = '';
            let city = '';

            // If principal care giver is not same as clinic care giver, add it
            if (clinic.careGiver !== clinic.principalCareGiver) {
                const principalCaregiver = careGivers.find(({id}) => clinic && id === clinic.principalCareGiver);
                if (principalCaregiver?.label !== undefined) {
                    careGiverName += `${principalCaregiver.label}, `;
                }
            }

            const clinicCaregiver = careGivers.find(({id}) => clinic && id === clinic.careGiver);
            if (clinicCaregiver?.label !== undefined) {
                careGiverName += `${clinicCaregiver.label}, `;
            }

            if (clinic.city) {
                city = `, ${clinic.city}`;
            }

            if (municipality?.name !== undefined && county?.name !== undefined) {
                location = ' (' + municipality?.name + ', ' + county?.name + ')';
            }

            return `${t('meta.clinic.welcome_to')} ${careGiverName}${clinic.name}${city}${location}.`;
        };

        const generateDescriptionText = (
            clinic: ClinicJSON,
            careGivers: CaregiverObject[],
            visitReasons: ReasonFilterObject[],
            county: CountyFilterObject | null,
            municipality: MunicipalityFilterObject | null,
            appointmentCategories: Array<AppointmentCategory>
        ): string => {
            let descriptionText = '';

            // Add clinic related information
            descriptionText += clinicInfoText(clinic, careGivers, county, municipality);

            // Add service related items
            descriptionText += getClinicServicesText(clinic.availableVisitReasons, clinic.hasDropin, visitReasons);

            // Append clinic welcome text if it exists
            descriptionText += clinic?.welcomeText ? ' ' + clinic?.welcomeText : '';

            return descriptionText;
        };

        const descriptionElement = document.querySelector('meta[name=description]');
        if (descriptionElement) {
            const descriptionText = generateDescriptionText(
                clinic,
                careGivers,
                visitReasons,
                county,
                municipality,
                appointmentCategories
            );
            descriptionElement?.setAttribute('content', descriptionText.replace(/(\r\n|\n|\r)/gm, ' '));
        }

        const titleElement = document.querySelector('title');
        if (titleElement) {
            titleElement.text = `${clinic.name} - MittVaccin.se`;
        }
    }, [clinic, loading, careGivers, visitReasons, appointmentCategories, county, municipality]);

    useEffect(() => {
        if (sessionState.hasActiveSession && sessionState.sessionTimedOut) {
            window.scrollTo(0, 0);
        }
    }, [sessionState.hasActiveSession, sessionState.sessionTimedOut]);

    useEffect(() => {
        if (healthDeclarationMode) {
            dispatch({type: BookingActions.BOOKING_HEALTH_DECLARATION_MODE});
        }
    }, [dispatch, healthDeclarationMode]);

    useEffect(() => {
        if (!clinic || error !== null) {
            return;
        }

        if (healthDeclarationMode && !clinic.hasHealthDeclarationInbox) {
            clinicDispatch({
                type: DataActions.SET_ERROR,
                value: new NoHealthDeclarationError('NoHealthDeclarationError')
            });
        }
    }, [clinic, clinicDispatch, dispatch, error, healthDeclarationMode]);

    // Google Tag Manager
    useEffect(() => {
        if (clinic === null || !clinic.googleAnalyticsTag) {
            return;
        }
        if (!hasConsent || document.querySelector('#gtm-script') !== null) {
            return;
        }
        gTagManager().initiate(clinic);
        gTagManager().updateConsent(hasConsent);

        return () => {
            gTagManager().removeScript();
        };
    }, [clinic, hasConsent]);

    useEffect(() => {
        if (clinic === null || !clinic.stylesheetUrl) {
            return;
        }

        return injectCustomCSSLink(clinic.stylesheetUrl);
    }, [clinic]);

    // Reset booking data
    useEffect(() => {
        if (clinic === null || !clinic.googleAnalyticsTag) {
            return;
        }
        dispatch({type: BookingActions.RESET_BOOKING});
        dispatch({type: BookingActions.RESET_BOOKING_ID});
    }, [clinic, dispatch]);
    if (sessionState.hasActiveSession && sessionState.sessionTimedOut) {
        return (
            <div className='container error-container'>
                <main className='main-content error-container__content' id='main'>
                    <section aria-live='assertive' aria-atomic>
                        <h1 className='error-container__header'>{t(`session.${bookingType}.expired_heading`)}</h1>
                        <p className='error-container__info-text'>{t(`session.${bookingType}.expired_content`)}</p>
                        <Button className='error-container__button' big onClick={() => window.location.reload()}>
                            <span>{t(`session.${bookingType}.expired_button`)}</span>
                        </Button>
                    </section>
                </main>
                <ClinicFooter />
            </div>
        );
    }

    return (
        <div className={`container${stepClasses}`} aria-busy={loading}>
            {!clinic?.externalBookingUrl && sessionState.hasActiveSession && bookingSessionState.showTimeoutWarning && (
                <ModalPortal>
                    <SessionWarningModal
                        refreshCallback={() => {
                            refreshBookingSession(bookingId);
                        }}
                    />
                </ModalPortal>
            )}
            {!clinic?.externalBookingUrl && bookingId && bookingSessionState.showOpenBookingTimeoutWarning && (
                <ModalPortal>
                    <OpenSessionWarningModal
                        onClose={() => {
                            bookingSessionDispatch({
                                type: BookingSessionActions.SET_SHOW_OPEN_BOOKING_TIMEOUT_WARNING,
                                value: false
                            });
                        }}
                    />
                </ModalPortal>
            )}
            {!clinic?.externalBookingUrl &&
                caregiver &&
                caregiver.cookiePolicyVersion &&
                clinic?.googleAnalyticsTag &&
                setCookieConsent !== undefined && (
                    <CookieConsentModal
                        caregiver={caregiver}
                        hasChosen={hasChosen}
                        setCookieConsent={setCookieConsent}
                    />
                )}
            <ClinicHeader />
            {error && <ClinicError bookingType={bookingType} error={error} />}
            {loading && <ClinicLoading />}
            {!loading && !error && (
                <>
                    <ClinicMain />
                    <ClinicAside />
                </>
            )}
            {!loading && <ClinicFooter />}
        </div>
    );
}
